import StatefulSettings from "../controllers/StatefulSettings";
import {
  ITaskTimerInfo,
  IPenaltyTimerInfo,
  VoteTask,
  AuditMessage,
  IShowNotificationState,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  TraceLevel,
} from "../ts/enums";
import { NotificationType } from "../controllers/messaging";
import {
  getDefaultVoteCount,
  getVoteCountType,
  isTimedItem,
} from "../utilities/utils";
import {
  handleDiscordVoteTicker,
  handleDiscordLovenseTicker,
} from "../utilities/discord";
import { differenceInMinutes } from "date-fns";

/**
 * VotingManager - Handles all voting-related operations including:
 * - Sending tasks to voting
 * - Checking for completed vote tasks
 * - Vote task state management
 * - Discord integration for votes
 */
export class VotingManager {
  private settings!: StatefulSettings;
  private showMessage!: (args: IShowNotificationState) => void;

  /**
   * Initialize the VotingManager with required dependencies
   */
  public initialize(
    settings: StatefulSettings,
    showMessage: (args: IShowNotificationState) => void
  ): void {
    this.settings = settings;
    this.showMessage = showMessage;
  }

  /**
   * Sends a task to voting for owner approval
   */
  public sendTaskToVoting(
    item: ITaskTimerInfo | IPenaltyTimerInfo,
    requiredMinutes: number
  ): void {
    //TaskItem.IsVotable(item) && put back to .votable as seems like this check has unintentionally changed items being sent to vote
    if (item.votable && isTimedItem(item.countType) && item.expectedCount) {
      const voteTask = new VoteTask({
        ...item,
        ...this.settings.getControl(),
        initialMinutes: requiredMinutes,
        durationMinutes: requiredMinutes,
        startDateTime: new Date(),
        votedMembers: {},
        voteCount: getDefaultVoteCount(item),
        countType: getVoteCountType(item.countType),
      });
      try {
        this.settings.updateVoteTask(voteTask);
      } catch (error: unknown) {
        console.error("Failed to update vote task:", error);
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            item.name || "Unknown Task",
            `Failed to update vote task: ${error instanceof Error ? error.message : String(error)}`,
            TraceLevel.error
          )
        );
      }
      handleDiscordVoteTicker(this.settings, voteTask);
      if (item.lovenseToyId) {
        this.settings.testToy(item.lovenseToyId);
        this.showMessage({
          message:
            "Your toy should have just received a test vibration. If not, Sync and Test toy in Lovense page",
          notificationType: NotificationType.ToyInfo,
        });
        handleDiscordLovenseTicker(this.settings, voteTask);
      }
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          `The item ${item.name} has been sent to voting`,
          TraceLevel.info
        )
      );
    }
  }

  /**
   * Checks for completed vote tasks and updates their state
   */
  public checkForCompleteTaskVotes(): void {
    if (this.settings.pendingRoll()) return;
    if (!this.settings.isLoaded()) return;

    const taskSchedule = this.settings.getTaskSchedule();
    const votingTasks = this.settings.getVotes();
    const myTasks = votingTasks.filter(
      (t) => t.userid === this.settings.getControl().userid
    );
    const completeTasks: VoteTask[] = [];

    myTasks.forEach((t) => {
      const match = taskSchedule[t.key];
      // invalid vote task
      if (!match) {
        completeTasks.push(t);
        return;
      }
      if (match.isComplete) {
        completeTasks.push(t);
        return;
      }
      if (!match.endTime || match.isPaused) return; // still not updated that it is started

      const existingEndTime = new Date(match.endTime);
      const voteEndTime = t.getEndTime();

      // Respect explicit live shortening (e.g., debug speed-up) instead of restoring vote duration.
      const hasSpeedUpOverride = typeof match.timerTicks === "number" && match.timerTicks <= 1;
      if (hasSpeedUpOverride && existingEndTime < voteEndTime) {
        const timerTicks = differenceInMinutes(existingEndTime, new Date());
        if (timerTicks <= 0) {
          completeTasks.push(t);
          return;
        }
        match.timerTicks = timerTicks;
        try {
          this.settings.updateTaskState(match);
        } catch (error: unknown) {
          console.error("Failed to update task state:", error);
        }
        return;
      }

      match.expectedCount = t.durationMinutes;
      match.endTime = voteEndTime;
      // completed vote task
      const timerTicks = differenceInMinutes(match.endTime, new Date());
      if (timerTicks <= 0) {
        completeTasks.push(t);
        return;
      }
      match.timerTicks = timerTicks;
      try {
        this.settings.updateTaskState(match);
      } catch (error: unknown) {
        console.error("Failed to update task state:", error);
      }
    });

    if (completeTasks.length > 0)
      this.settings.clearCompleteVoteTasks(completeTasks);
  }
}
